// Read-only reference page documenting which role can access which area
// of /admin and which user-row actions. Section 1 is derived from
// TAB_MIN_ROLE; Section 2 is hand-curated to mirror the UI guards in
// AdminPage.tsx (e.g. Impersoneer / Verwijder buttons are wrapped in
// `{isSuperuser ? ... : null}`).

import { type AccessRole } from "@/lib/hooks/useAuth";
import { isTabVisible, TAB_MIN_ROLE, TAB_TITLE, type Tab } from "./adminTabs";
import { SIDEBAR_GROUPS } from "./sidebarConfig";

const ROLE_COLUMNS: {
  role: AccessRole;
  label: string;
  rank: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  dim?: boolean;
  description: string;
}[] = [
  {
    role: "user",
    label: "User",
    rank: 1,
    dim: true,
    description: "Eigen opnames en todo's. Verder geen /admin.",
  },
  {
    role: "it_admin",
    label: "IT-beheerder",
    rank: 2,
    description:
      "IT-beheer van de eigen organisatie: app-versies, hostnames en widget-logs. Geen gebruikersbeheer, geen opnames.",
  },
  {
    role: "association_admin",
    label: "Associatie",
    rank: 3,
    description: "Associatiescope. Heeft vandaag geen eigen /admin-pagina's.",
  },
  {
    role: "afdeling_secretary",
    label: "Secretaris",
    rank: 4,
    description: "Afdelingssecretariaat: afdelingsopnames bekijken en bewerken, plus todo's.",
  },
  {
    role: "department_admin",
    label: "Afdeling",
    rank: 5,
    description: "Afdelingsbeheer: afdelingsopnames bekijken en bewerken.",
  },
  {
    role: "hospital_admin",
    label: "Hospital",
    rank: 6,
    description:
      "Organisatiebeheer: gebruikers, afdelingen, templates, statistieken en het IT-overzicht.",
  },
  {
    role: "superuser",
    label: "Superuser",
    rank: 7,
    description: "Volledige toegang, inclusief impersoneren en verwijderen.",
  },
];

const RANK_BAR: Record<1 | 2 | 3 | 4 | 5 | 6 | 7, string> = {
  1: "bg-[var(--cs-slate-soft)] opacity-55",
  2: "bg-[var(--cs-slate-soft)] opacity-62",
  3: "bg-[var(--cs-slate-soft)] opacity-70",
  4: "bg-[var(--cs-slate)] opacity-70",
  5: "bg-[var(--cs-navy-soft)]",
  6: "bg-[var(--cs-navy)]",
  7: "bg-[var(--cs-rose)] shadow-[0_0_0_2px_rgba(225,29,72,0.10)]",
};

type Row = { key: string; label: string; sub: string; allowed: AccessRole[] };

// Section 2 — hand-curated user-row actions.
const USER_ACTION_ROWS: Row[] = [
  {
    key: "view-list",
    label: "Gebruikerslijst bekijken",
    sub: "Bekijken",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "edit-profile",
    label: "Profielvelden bewerken",
    sub: "Naam, e-mail, goedkeuring",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "assign-scope",
    label: "Organisatie / afdeling / associatie toewijzen",
    sub: "Scope",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "change-role",
    label: "Toegangsrol wijzigen",
    sub: "Gevoelig",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "edit-plan",
    label: "Plan & trial-einddatum wijzigen",
    sub: "Plan",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "approve-verify",
    label: "Goedkeuren / e-mail verifiëren",
    sub: "Onboarding",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "passkey-reset",
    label: "Passkey reset versturen",
    sub: "Auth",
    allowed: ["hospital_admin", "superuser"],
  },
  {
    key: "impersonate",
    label: "Impersoneren",
    sub: "Gevoelig",
    allowed: ["superuser"],
  },
  {
    key: "delete",
    label: "Verwijderen",
    sub: "Destructief",
    allowed: ["superuser"],
  },
  {
    key: "dept-recordings",
    label: "Afdelingsopnames bekijken & bewerken",
    sub: "Opnames",
    allowed: ["afdeling_secretary", "department_admin", "superuser"],
  },
];

type PageRow = { key: Tab; label: string; sub: string };

// Section 1 — walk the sidebar groups in visual order so the matrix
// matches the order users see in the navigation.
function buildPageRows(): PageRow[] {
  const rows: PageRow[] = [];
  for (const group of SIDEBAR_GROUPS) {
    const category = group.caption ?? "Hoofd";
    for (const item of group.items) {
      // sidebarConfig's AdminTab union is a superset of TAB_MIN_ROLE's
      // keys today, but guard anyway to be future-proof.
      if (!(item.key in TAB_MIN_ROLE)) continue;
      rows.push({ key: item.key as Tab, label: TAB_TITLE[item.key as Tab], sub: category });
    }
  }
  return rows;
}

export function AdminRolesPermissionsTab() {
  const pageRows = buildPageRows();

  return (
    <div className="-mx-6 -my-6 min-h-full bg-[radial-gradient(1100px_700px_at_88%_-120px,rgba(225,29,72,0.045),transparent_60%),radial-gradient(900px_600px_at_-8%_12%,rgba(10,52,96,0.05),transparent_65%)] px-6 py-6">
      <PageHeader />
      <RoleKey />
      <MatrixSection
        sectionNum="01"
        title="Admin-pagina's"
        count={`${pageRows.length} paginas`}
        roleColumnLabel="Pagina"
        rows={pageRows.map((r) => ({
          key: r.key,
          label: r.label,
          sub: r.sub,
          allowed: ROLE_COLUMNS.filter((col) => isTabVisible(col.role, r.key)).map((col) => col.role),
        }))}
      />
      <MatrixSection
        sectionNum="02"
        title="Gebruikersacties op /admin → Gebruikers"
        count={`${USER_ACTION_ROWS.length} acties`}
        roleColumnLabel="Actie"
        rows={USER_ACTION_ROWS}
      />
      <p className="mt-6 cs-mono text-[11px] tracking-wide text-[var(--cs-ink-subtle)]">
        Bron:{" "}
        <code className="rounded bg-[rgba(10,52,96,0.06)] px-1.5 py-0.5 text-[10.5px]">
          TAB_MIN_ROLE
        </code>{" "}
        en{" "}
        <code className="rounded bg-[rgba(10,52,96,0.06)] px-1.5 py-0.5 text-[10.5px]">
          TAB_ALLOWED_ROLES
        </code>{" "}
        in{" "}
        <code className="rounded bg-[rgba(10,52,96,0.06)] px-1.5 py-0.5 text-[10.5px]">
          web/src/routes/admin/adminTabs.ts
        </code>{" "}
        · UI-guards op de Gebruikers-tab · alleen-lezen referentie, alleen zichtbaar voor superusers.
      </p>
    </div>
  );
}

function PageHeader() {
  return (
    <header className="mb-10 grid grid-cols-1 items-end gap-8 md:grid-cols-[1fr_auto]">
      <div>
        <p className="cs-caption flex items-center gap-2.5">
          <span aria-hidden className="inline-block h-px w-[22px] bg-[var(--cs-rose)]" />
          Configuratie · Referentie
        </p>
        <h1 className="mt-3.5 mb-2 text-[34px] font-semibold leading-[1.05] tracking-[-0.012em] text-[var(--cs-navy)]">
          Rollen &amp; <em className="not-italic text-[var(--cs-rose)]">rechten</em>
        </h1>
        <p className="m-0 max-w-[62ch] text-[14.5px] leading-[1.55] text-[var(--cs-ink-muted)]">
          Wie ziet wat, en wie mag wat aanpassen. De matrix wordt opgebouwd uit{" "}
          <code className="cs-mono rounded bg-[rgba(10,52,96,0.06)] px-1.5 py-0.5 text-[12px]">
            TAB_MIN_ROLE
          </code>{" "}
          en de huidige UI-regels op de <em className="not-italic font-medium">Gebruikers</em>-tab.
          Lees als een ladder: hoe meer rose, hoe meer rechten. Twee pagina&apos;s doorbreken die
          ladder bewust &mdash; <em className="not-italic font-medium">IT-overzicht</em> en{" "}
          <em className="not-italic font-medium">Todo&apos;s</em> hebben een eigen lijst met
          toegestane rollen, dus een lagere rol kan daar toegang hebben waar een hogere die mist.
        </p>
      </div>

      <aside
        aria-label="Legenda"
        className="flex min-w-[220px] flex-col gap-2 rounded-[14px] border border-[var(--cs-rule)] bg-white/70 px-4 py-3.5 backdrop-blur-md"
      >
        <span className="cs-caption">Legenda</span>
        <div className="flex items-center gap-2.5 text-[12.5px] text-[var(--cs-ink)]">
          <span className="inline-flex w-[18px] justify-center">
            <Mark allowed />
          </span>
          <span>Toegang</span>
        </div>
        <div className="flex items-center gap-2.5 text-[12.5px] text-[var(--cs-ink)]">
          <span className="inline-flex w-[18px] justify-center">
            <Mark allowed={false} />
          </span>
          <span>Geen toegang</span>
        </div>
      </aside>
    </header>
  );
}

function RoleKey() {
  return (
    <section aria-label="Wat de rollen betekenen" className="mb-11">
      <div className="flex items-baseline justify-between gap-4 border-b border-[var(--cs-rule)] pb-3.5">
        <div>
          <p className="cs-caption text-[var(--cs-rose)]">Sleutel</p>
          <h2 className="mt-1.5 text-[18px] font-semibold tracking-[-0.005em] text-[var(--cs-navy)]">
            De rollen
          </h2>
        </div>
        <span className="cs-mono text-[11px] tabular-nums text-[var(--cs-ink-subtle)]">
          {ROLE_COLUMNS.length} rollen
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-0 md:grid-cols-2">
        {ROLE_COLUMNS.map((col) => (
          <div
            key={col.role}
            className="flex gap-3.5 border-t border-[var(--cs-rule)] py-3 first:border-t-0 md:[&:nth-child(2)]:border-t-0"
          >
            <span
              aria-hidden
              className={`mt-[7px] h-[3px] w-9 shrink-0 rounded-full ${RANK_BAR[col.rank]}`}
            />
            <div>
              <dt
                className={`cs-mono text-[11px] uppercase tracking-[0.08em] ${
                  col.dim ? "text-[var(--cs-ink-muted)]" : "text-[var(--cs-navy)]"
                }`}
              >
                {col.label}
              </dt>
              <dd className="mt-1 m-0 max-w-[46ch] text-[13px] leading-[1.5] text-[var(--cs-ink-muted)]">
                {col.description}
              </dd>
            </div>
          </div>
        ))}
      </dl>
    </section>
  );
}

function MatrixSection({
  sectionNum,
  title,
  count,
  roleColumnLabel,
  rows,
}: {
  sectionNum: string;
  title: string;
  count: string;
  roleColumnLabel: string;
  rows: Row[];
}) {
  return (
    <section className="mt-11 first:mt-0">
      <div className="flex items-baseline justify-between gap-4 border-b border-[var(--cs-rule)] pb-3.5">
        <div>
          <p className="cs-caption text-[var(--cs-rose)]">Sectie {sectionNum}</p>
          <h2 className="mt-1.5 text-[18px] font-semibold tracking-[-0.005em] text-[var(--cs-navy)]">
            {title}
          </h2>
        </div>
        <span className="cs-mono text-[11px] tabular-nums text-[var(--cs-ink-subtle)]">
          {count}
        </span>
      </div>

      <div className="mt-4 overflow-hidden rounded-[18px] border border-[var(--cs-rule)] bg-white/80 shadow-[0_1px_2px_rgba(10,52,96,0.04),0_10px_28px_-18px_rgba(10,52,96,0.12)] backdrop-blur-md">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="w-[42%] bg-gradient-to-b from-[rgba(252,251,247,0.6)] to-transparent px-6 pt-[18px] pb-3.5 text-left align-bottom font-medium text-[var(--cs-navy)]"
                >
                  {roleColumnLabel}
                </th>
                {ROLE_COLUMNS.map((col) => (
                  <th
                    key={col.role}
                    scope="col"
                    className="w-[11.6%] whitespace-nowrap bg-gradient-to-b from-[rgba(252,251,247,0.6)] to-transparent px-3.5 pt-[18px] pb-3.5 text-center align-bottom font-medium"
                  >
                    <span
                      title={col.description}
                      className={`cs-mono mb-2 block text-[11px] uppercase tracking-[0.08em] ${
                        col.dim ? "text-[var(--cs-ink-muted)]" : "text-[var(--cs-navy)]"
                      }`}
                    >
                      {col.label}
                    </span>
                    <span
                      aria-hidden
                      className={`mx-auto block h-[3px] w-9 rounded-full ${RANK_BAR[col.rank]}`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row.key}
                  className={`border-t border-[var(--cs-rule)] transition-colors hover:bg-[rgba(225,29,72,0.03)] ${
                    idx % 2 === 1 ? "bg-[rgba(252,251,247,0.55)]" : ""
                  }`}
                  style={{
                    animation: "row-rise 360ms cubic-bezier(0.2,0.8,0.2,1) both",
                    animationDelay: `${Math.min(20 + idx * 30, 380)}ms`,
                  }}
                >
                  <td className="px-6 py-3.5 align-middle">
                    <div className="font-medium leading-tight text-[var(--cs-navy)]">
                      {row.label}
                    </div>
                    <span className="cs-mono mt-1 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.08em] text-[var(--cs-ink-subtle)] before:inline-block before:h-1 before:w-1 before:rounded-full before:bg-[rgba(10,52,96,0.28)] before:content-['']">
                      {row.sub}
                    </span>
                  </td>
                  {ROLE_COLUMNS.map((col) => (
                    <td key={col.role} className="px-3.5 py-3.5 text-center align-middle">
                      <Mark allowed={row.allowed.includes(col.role)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function Mark({ allowed }: { allowed: boolean }) {
  if (allowed) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full">
        <span className="block h-2 w-2 rounded-full bg-[var(--cs-rose)] shadow-[0_0_0_5px_rgba(225,29,72,0.10)]" />
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center text-[14px] text-[rgba(10,52,96,0.28)]">
      —
    </span>
  );
}
